import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { C } from "../lib/constants";
import { normalizeMode } from "../lib/helpers";
import { Btn } from "./ui/Btn";
import { TopNav } from "./ui/TopNav";
import { ClienteLoginScreen } from "./AuthScreens";

export const CuentaScreen = ({setScreen, user, profile, activeMode, setActiveMode, onLogout}) => {
  const [perfilTrabajador, setPerfilTrabajador] = useState(null);
  const [perfilCliente, setPerfilCliente] = useState(null);
  const [cargandoPerfiles, setCargandoPerfiles] = useState(true);

  useEffect(() => {
    if (!user?.id) { setCargandoPerfiles(false); return; }
    const timer = setTimeout(() => setCargandoPerfiles(false), 6000);

    const cargarPerfiles = async () => {
      try {
        // FIX SESIÓN CRUZADA: SOLO filtrar por user_id del usuario autenticado actual.
        // Eliminados fallbacks por teléfono — podían cargar perfiles de otra cuenta
        // si dos usuarios tenían el mismo número registrado.
        const { data: authData } = await supabase.auth.getUser();
        const currentUserId = authData?.user?.id;
        if (!currentUserId || currentUserId !== user.id) {
          // Seguridad extra: si el user de la sesión no coincide con el prop, no cargar nada
          console.warn("FICIO: Discrepancia de sesión detectada en CuentaScreen");
          setCargandoPerfiles(false);
          return;
        }

        const [resT, resC] = await Promise.all([
          supabase.from("trabajadores")
            .select("id,nombre,oficio,plan,verificado,membresia_activa,membresia,fecha_vencimiento,user_id")
            .eq("user_id", currentUserId)
            .neq("archivado", true)
            .maybeSingle(),
          supabase.from("clientes")
            .select("id,nombre,user_id")
            .eq("user_id", currentUserId)
            .neq("archivado", true)
            .maybeSingle(),
        ]);

        const perfilWorker = resT.data || null;

        setPerfilTrabajador(perfilWorker);
        setPerfilCliente(resC.data || null);

        // FIX CACHÉ: si el activeMode es "trabajador" pero no hay perfil real en DB,
        // forzar modo "cliente" y limpiar el valor inválido del localStorage.
        // Esto corrige el bug donde "Modo activo: Maestro" pero "Maestro → + Registrarme".
        if (!perfilWorker) {
          setActiveMode?.("cliente", user?.id || null);
        }
      } catch(e) {
        console.warn("Error cargando perfiles:", e.message);
      } finally {
        clearTimeout(timer);
        setCargandoPerfiles(false);
      }
    };

    cargarPerfiles();
  }, [user?.id, setActiveMode]); // FIX AUDIT: profile?.telefono eliminado de deps — user?.id es suficiente para recargar perfiles

  if (!user) return <ClienteLoginScreen setScreen={setScreen} onAuthReady={()=>{}}/>;

  const nombre     = profile?.nombre || user?.email || "";
  const tieneRolTrabajador = Boolean(perfilTrabajador);
  const tieneRolCliente    = Boolean(perfilCliente);
  // FIX CACHÉ: esTrabajador se basa en el perfil real de DB (tieneRolTrabajador)
  // combinado con el activeMode. Si activeMode es "trabajador" pero no hay perfil en DB,
  // tratar como cliente para no mostrar roles fantasma de cuentas borradas.
  const esTrabajador = tieneRolTrabajador && normalizeMode(activeMode) === "trabajador";

  const MEM = {
    activa:   { bg: C.greenL,   color: C.green,   label: "✓ Activa",   desc: (w) => `${w?.oficio || "Oficio registrado"} · Plan ${w?.plan || "Básico"}` },
    inactiva: { bg: C.redL,     color: C.red,      label: "Vencida",    desc: () => "Tu membresía venció · Contáctanos por WhatsApp para renovar" },
    prueba:   { bg: "#FFF3E0",  color: "#E65100",  label: "En prueba",  desc: () => "Tu cuenta está en revisión · Te contactamos por WhatsApp para activarla" },
  };

  const cambiarRol = (nuevoRol) => {
    const modo = normalizeMode(nuevoRol);
    // Si quiere trabajador pero no tiene perfil completo → completar registro
    if (modo === "trabajador" && !tieneRolTrabajador) {
      setScreen("reg-worker"); return;
    }
    if (modo === "cliente" && !tieneRolCliente) {
      setScreen("reg-client"); return;
    }
    setActiveMode?.(modo, user?.id || null);
    supabase.from("perfiles")
      .upsert({id:user.id, user_id:user.id, modo_activo:modo, updated_at:new Date().toISOString()},{onConflict:"user_id"})
      .then(({error})=>{ if(error) console.warn("modo:", error.message); });
  };

  return (
    <div style={{minHeight:"100vh",background:C.bg,paddingBottom:110}}>
      <TopNav setScreen={setScreen} back="landing" backLabel="Inicio" title="Mi cuenta"/>
      <div style={{maxWidth:600,margin:"0 auto",padding:"20px"}}>

        {/* Header */}
        <div style={{background:`linear-gradient(135deg,${esTrabajador?C.accent:C.blue},${esTrabajador?"#7C4A22":"#123E73"})`,borderRadius:24,padding:22,color:"#fff",marginBottom:16,boxShadow:"0 8px 28px rgba(0,0,0,.14)"}}>
          <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8}}>
            <div style={{width:52,height:52,borderRadius:16,background:"rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,flexShrink:0}}>
              {esTrabajador?"🔨":"🏠"}
            </div>
            <div>
              <div style={{fontFamily:"Georgia,serif",fontSize:20,fontWeight:900}}>{nombre}</div>
              <div style={{fontSize:12,opacity:.88,marginTop:2}}>{user?.email}</div>
            </div>
          </div>
          <div style={{background:"rgba(255,255,255,0.15)",borderRadius:10,padding:"8px 12px",display:"inline-block"}}>
            <span style={{fontSize:12,fontWeight:700}}>Modo activo: {esTrabajador?"Maestro 🔨":"Cliente 🏠"}</span>
          </div>
        </div>

        {/* Mis roles */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:16,marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
          <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:.8,textTransform:"uppercase",marginBottom:12}}>Mis roles en Ficio</div>

          {cargandoPerfiles ? (
            <div style={{color:C.muted,fontSize:13,textAlign:"center",padding:"8px 0"}}>Cargando...</div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {/* Rol Cliente */}
              <div onClick={()=>cambiarRol("cliente")}
                style={{border:`2px solid ${!esTrabajador?C.blue:C.border}`,borderRadius:14,padding:"14px 16px",cursor:"pointer",background:!esTrabajador?C.blueL:C.bg,transition:"all 0.2s",display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:42,height:42,borderRadius:12,background:!esTrabajador?C.blue:`${C.blue}20`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>🏠</div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                    <span style={{fontFamily:"Georgia,serif",fontSize:15,fontWeight:800,color:C.text}}>Cliente</span>
                    {tieneRolCliente
                      ? <span style={{background:C.greenL,color:C.green,fontSize:10,fontWeight:700,borderRadius:6,padding:"2px 6px"}}>✓ Activo</span>
                      : <span style={{background:C.accentL,color:C.accent,fontSize:10,fontWeight:700,borderRadius:6,padding:"2px 6px"}}>+ Registrarme</span>
                    }
                  </div>
                  <div style={{color:C.muted,fontSize:11}}>
                    {tieneRolCliente ? "Busca maestros · Gestiona tus obras" : "Toca para registrarte como cliente"}
                  </div>
                </div>
                {!esTrabajador && <div style={{width:8,height:8,borderRadius:"50%",background:C.blue,flexShrink:0}}/>}
              </div>

              {/* Rol Maestro */}
              <div onClick={()=>cambiarRol("trabajador")}
                style={{border:`2px solid ${esTrabajador?C.accent:C.border}`,borderRadius:14,padding:"14px 16px",cursor:"pointer",background:esTrabajador?C.accentL:C.bg,transition:"all 0.2s",display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:42,height:42,borderRadius:12,background:esTrabajador?C.accent:`${C.accent}20`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>🔨</div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:2}}>
                    <span style={{fontFamily:"Georgia,serif",fontSize:15,fontWeight:800,color:C.text}}>Maestro</span>
                    {tieneRolTrabajador
                      ? (() => { const m = MEM[perfilTrabajador?.membresia] || MEM.prueba; return <span style={{background:m.bg,color:m.color,fontSize:10,fontWeight:700,borderRadius:6,padding:"2px 6px"}}>{m.label}</span>; })()
                      : <span style={{background:C.accentL,color:C.accent,fontSize:10,fontWeight:700,borderRadius:6,padding:"2px 6px"}}>+ Registrarme</span>
                    }
                  </div>
                  <div style={{color:C.muted,fontSize:11}}>
                    {tieneRolTrabajador
                      ? (MEM[perfilTrabajador?.membresia] || MEM.prueba).desc(perfilTrabajador)
                      : "Toca para registrarte como maestro en Ficio"}
                  </div>
                </div>
                {esTrabajador && <div style={{width:8,height:8,borderRadius:"50%",background:C.accent,flexShrink:0}}/>}
              </div>
            </div>
          )}
        </div>

        {/* Accesos rápidos según rol activo */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:16,marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
          <div style={{color:C.sub,fontSize:11,fontWeight:800,letterSpacing:.8,textTransform:"uppercase",marginBottom:12}}>Acceso rápido</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Btn full color={esTrabajador?C.accent:C.blue} onClick={()=>setScreen("obra-activa")}>
              {esTrabajador?"📋 Mis trabajos":"🏗️ Mi obra activa"}
            </Btn>
            <Btn full outline color={C.blue} onClick={()=>setScreen("directory")}>🔍 Directorio</Btn>
            {tieneRolTrabajador && (
              <Btn full outline color={C.accent} onClick={()=>setScreen("solicitudes-worker")}>📋 Mis solicitudes</Btn>
            )}
            {tieneRolTrabajador && (
              <Btn full outline color={C.accent} onClick={()=>setScreen("editar-maestro")}>✏️ Editar perfil maestro</Btn>
            )}
            {tieneRolCliente && (
              <Btn full outline color={C.blue} onClick={()=>setScreen("trabajos-cliente")}>📂 Mis trabajos</Btn>
            )}
            {tieneRolCliente && (
              <Btn full outline color={C.blue} onClick={()=>setScreen("perfil-cliente")}>👤 Mi perfil cliente</Btn>
            )}
          </div>
        </div>

        {/* Cerrar sesión */}
        <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:18,padding:16,boxShadow:"0 2px 8px rgba(0,0,0,.04)"}}>
          <div style={{color:C.sub,fontSize:13,lineHeight:1.5,marginBottom:14}}>La sesión no se cierra al navegar. Usa este botón cuando quieras salir.</div>
          <Btn full color={C.red} onClick={onLogout}>🚪 Cerrar sesión</Btn>
        </div>

        {/* Acceso admin — discreto, protegido con contraseña en el panel */}
        <div style={{textAlign:"center",marginTop:20}}>
          <button onClick={()=>setScreen("admin")}
            style={{background:"none",border:"none",color:C.muted,fontSize:11,cursor:"pointer",textDecoration:"underline",fontFamily:"inherit"}}>
            Administración
          </button>
        </div>
      </div>
    </div>
  );
};
